/**
 * Chat Hook
 * Custom hook for managing chat messages with persistence
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  chatMessagesAtom,
  hasMessagesAtom,
  lastMessageAtom,
  messageCountAtom,
  addMessageAtom,
  setMessagesAtom,
  clearMessagesAtom,
  updateMessageAtom,
  removeMessageAtom
} from '@/stores/chat.store';
import type { ChatMessage, AttachmentMeta } from '@/types/websocket.types';
import type { Message } from '@/modules/chatbot/types';
import { sanitizeTextInput } from '@/lib/sanitize';

export interface UseChatReturn {
  // State
  messages: ChatMessage[];
  hasMessages: boolean;
  lastMessage: ChatMessage | null;
  messageCount: number;

  // Actions
  addMessage: (message: ChatMessage) => void;
  addUserMessage: (content: string, channelId?: string, attachments?: AttachmentMeta[]) => ChatMessage;
  /** An agent turn authored in the thread, the way a customer turn is. */
  addAssistantMessage: (content: string) => ChatMessage;
  /** What a run answered with. Shown like any other turn, kept like none of them. */
  addRunReply: (content: string, rules?: ChatMessage['rules'], requestId?: string, isError?: boolean) => ChatMessage;
  setMessages: (messages: ChatMessage[]) => void;
  clearMessages: () => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
}

/**
 * Generate a unique message ID
 */
export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

export function useChat(): UseChatReturn {
  // State
  const [messages] = useAtom(chatMessagesAtom);
  const hasMessages = useAtomValue(hasMessagesAtom);
  const lastMessage = useAtomValue(lastMessageAtom);
  const messageCount = useAtomValue(messageCountAtom);

  // Actions
  const addMessageAction = useSetAtom(addMessageAtom);
  const setMessagesAction = useSetAtom(setMessagesAtom);
  const clearMessagesAction = useSetAtom(clearMessagesAtom);
  const updateMessageAction = useSetAtom(updateMessageAtom);
  const removeMessageAction = useSetAtom(removeMessageAtom);

  const addMessage = useCallback(
    (message: ChatMessage) => {
      addMessageAction(message);
    },
    [addMessageAction]
  );

  const addUserMessage = useCallback(
    (content: string, channelId?: string, attachments?: AttachmentMeta[]): ChatMessage => {
      const message: ChatMessage = {
        id: generateMessageId(),
        from: 'user',
        content: sanitizeTextInput(content),
        timestamp: Date.now(),
        channelId,
        attachments,
      };
      addMessageAction(message);
      return message;
    },
    [addMessageAction]
  );

  const addAssistantMessage = useCallback(
    (content: string): ChatMessage => {
      const message: ChatMessage = {
        id: generateMessageId(),
        from: 'assistant',
        content,
        timestamp: Date.now()
      };
      addMessageAction(message);
      return message;
    },
    [addMessageAction]
  );

  const addRunReply = useCallback(
    (content: string, rules?: ChatMessage['rules'], requestId?: string, isError?: boolean): ChatMessage => {
      const message: ChatMessage = {
        id: generateMessageId(),
        from: 'assistant',
        content,
        timestamp: Date.now(),
        rules,
        requestId,
        isError,
        fromRun: true
      };
      addMessageAction(message);
      return message;
    },
    [addMessageAction]
  );

  const setMessages = useCallback(
    (newMessages: ChatMessage[]) => {
      setMessagesAction(newMessages);
    },
    [setMessagesAction]
  );

  const clearMessages = useCallback(() => {
    clearMessagesAction();
  }, [clearMessagesAction]);

  const updateMessage = useCallback(
    (id: string, updates: Partial<ChatMessage>) => {
      updateMessageAction({ id, updates });
    },
    [updateMessageAction]
  );

  const removeMessage = useCallback(
    (id: string) => {
      removeMessageAction(id);
    },
    [removeMessageAction]
  );

  return {
    // State
    messages,
    hasMessages,
    lastMessage,
    messageCount,

    // Actions
    addMessage,
    addUserMessage,
    addAssistantMessage,
    addRunReply,
    setMessages,
    clearMessages,
    updateMessage,
    removeMessage
  };
}

// ============================================================================
// Helper Hook for converting ChatMessage to Message format
// ============================================================================
/**
 * Convert ChatMessage array to Message array for chatbot component
 */
export function useChatMessages(): {
  messages: Message[];
  clearMessages: () => void;
} {
  const { messages: chatMessages, clearMessages } = useChat();

  const messages: Message[] = chatMessages.map((msg) => ({
    id: msg.id,
    content: msg.content,
    role: msg.from === 'user' ? 'user' : 'assistant',
    timestamp: new Date(msg.timestamp),
    channelId: msg.channelId,
    isError: msg.isError,
    attachments: msg.attachments,
    requestId: msg.requestId,
  }));

  return { messages, clearMessages };
}
